import { useEffect, useState } from "react";
import { Box, Card, Skeleton, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { SortIcon, Table } from "@APP/components";
import { API } from "@APP/services";
import { ReusableRTPPaymentSort } from "@APP/services/api/schemas/rtp";
import { Amount, ReusableRTPPayment, SortType } from "@APP/types";
import { formatCurrency, formatDisplayedDate } from "@APP/utils";

type Props = {
  rtpId: string;
  currency: string;
  allowTip: boolean;
  /**
   * The RTP's fixed requested (base) amount. The payments API doesn't return a
   * tip breakdown, so tip is derived as `totalAmountPaid - baseAmount`. When
   * absent (open-amount RTP), there's no baseline to derive a tip from, so the
   * Tip column is hidden entirely.
   */
  baseAmount?: number;
};

type PaymentOrderBy = "customerName" | "baseValue" | "tip" | "totalValue" | "receivedDate";

type HeadCell = { id: PaymentOrderBy; label: string; align?: "right" };

const useStyles = makeStyles((theme) => ({
  heading: {
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  card: {
    overflowX: "auto",
  },
  statusTag: {
    fontSize: 11,
    fontWeight: 600,
    color: "#7B8A9E",
  },
  cell: {
    whiteSpace: "nowrap",
  },
  loading: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const mapSortBy = (key: PaymentOrderBy): ReusableRTPPaymentSort["sort_by"] => {
  switch (key) {
    case "customerName":
      return "customer_name";
    case "baseValue":
      return "base_amount";
    case "tip":
      return "tip_amount";
    case "totalValue":
      return "total_amount_paid";
    case "receivedDate":
    default:
      return "payment_received_date";
  }
};

const PaymentsTable = ({ rtpId, currency, allowTip, baseAmount }: Props) => {
  const classes = useStyles();

  const [data, setData] = useState<ReusableRTPPayment[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [entries, setEntries] = useState(5);
  const [order, setOrder] = useState<SortType>(SortType.desc);
  const [orderBy, setOrderBy] = useState<PaymentOrderBy>("receivedDate");
  const [loading, setLoading] = useState(true);
  // Whether the last fetch failed. We deliberately don't surface the raw error
  // message (e.g. axios's "Network Error") — just show a contextual, friendly state.
  const [hasError, setHasError] = useState(false);

  const fetchPayments = async (
    nextPage: number,
    nextEntries: number,
    nextOrder: SortType,
    nextOrderBy: PaymentOrderBy,
  ) => {
    try {
      setLoading(true);
      setHasError(false);
      const { data: payments, meta } = await API.getReusableRTPPayments(
        rtpId,
        { page: nextPage, entries: nextEntries },
        { sort_by: mapSortBy(nextOrderBy), sort_direction: nextOrder },
      );
      setData(payments ?? []);
      setTotalItems(meta?.totalItems ?? payments?.length ?? 0);
    } catch {
      setHasError(true);
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
    setOrder(SortType.desc);
    setOrderBy("receivedDate");
    fetchPayments(0, entries, SortType.desc, "receivedDate");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtpId]);

  const showTipColumn = allowTip && baseAmount != null;

  /** Payments API has no tip field; tip = max(0, totalPaid - baseAmount). */
  const deriveTip = (payment: ReusableRTPPayment): number | undefined => {
    if (baseAmount == null) return undefined;
    const totalPaid = payment.totalAmountPaid?.amount ?? payment.amount?.amount;
    if (totalPaid == null) return undefined;

    return Math.max(0, parseFloat(totalPaid) - baseAmount);
  };

  const handleRequestSort = (property: PaymentOrderBy) => {
    const isAsc = orderBy === property && order === SortType.asc;
    const nextOrder = isAsc ? SortType.desc : SortType.asc;
    setOrder(nextOrder);
    setOrderBy(property);
    setPage(0);
    fetchPayments(0, entries, nextOrder, property);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchPayments(nextPage, entries, order, orderBy);
  };

  const handleEntriesChange = (nextEntries: number) => {
    setEntries(nextEntries);
    setPage(0);
    fetchPayments(0, nextEntries, order, orderBy);
  };

  const columns: HeadCell[] = [
    { id: "customerName", label: "Customer" },
    ...(baseAmount
      ? [{ id: "baseValue" as const, label: "Base Amount", align: "right" as const }]
      : []),
    ...(showTipColumn ? [{ id: "tip" as const, label: "Tip", align: "right" as const }] : []),
    { id: "totalValue", label: "Amount Paid", align: "right" },
    { id: "receivedDate", label: "Received" },
  ];

  const cur = (amt?: Amount) =>
    amt?.amount != null ? formatCurrency(amt.amount, amt.currency || currency) : "—";
  const curValue = (value?: number) =>
    value != null ? formatCurrency(value.toFixed(2), currency) : "—";

  const renderHeader = () => (
    <TableRow>
      {columns.map(({ id, label, align }) => (
        <TableCell
          key={id}
          align={align ?? "left"}
          className={classes.cell}
          onClick={() => (data.length ? handleRequestSort(id) : undefined)}
          style={{ cursor: data.length ? "pointer" : "default" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={align === "right" ? "flex-end" : "flex-start"}>
            {label}
            {!!data.length && <SortIcon active={orderBy === id} type={order} />}
          </Box>
        </TableCell>
      ))}
      {/* <TableCell className={classes.cell}>Status</TableCell> */}
    </TableRow>
  );

  const renderRows = (payment: ReusableRTPPayment) => {
    // const isReceived = payment.status === "PaymentReceived";
    const nameCell = (
      <Typography variant="body2" fontWeight={600} noWrap>
        {payment.payerName || "Unknown payer"}
      </Typography>
    );

    return (
      <TableRow key={payment.id} hover>
        <TableCell className={classes.cell}>
          {payment.paymentId ? (
            <Tooltip title={`Payment ID: ${payment.paymentId}`}>{nameCell}</Tooltip>
          ) : (
            nameCell
          )}
        </TableCell>
        {baseAmount && (
          <TableCell align="right" className={classes.cell}>
            {curValue(baseAmount)}
          </TableCell>
        )}
        {showTipColumn && (
          <TableCell align="right" className={classes.cell}>
            {curValue(deriveTip(payment))}
          </TableCell>
        )}
        <TableCell align="right" className={classes.cell}>
          {cur(payment.totalAmountPaid ?? payment.amount)}
        </TableCell>
        <TableCell className={classes.cell}>
          {formatDisplayedDate(payment.createdAt, "PPp") ?? "—"}
        </TableCell>
        {/* <TableCell className={classes.cell}>
          {!isReceived && <span className={classes.statusTag}>{payment.status}</span>}
        </TableCell> */}
      </TableRow>
    );
  };

  return (
    <Box>
      <Typography className={classes.heading}>Payments</Typography>

      {loading && (
        <Box className={classes.loading}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      )}

      {!loading && (
        <Card elevation={2} className={classes.card}>
          <Table
            data={data}
            renderHeader={renderHeader}
            renderRows={renderRows}
            renderEmptyDataRow={() => (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                  {hasError ? (
                    <>
                      <Typography variant="body2" color="error" fontWeight={600}>
                        Unable to load your payments.
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Please try again later or refresh the page.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight={600} color="textSecondary">
                        No Payments Received Yet
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Payments will appear here once they have been received.
                      </Typography>
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}
            showPagination={!hasError && !!data.length}
            entries={entries}
            page={page}
            lastPage={Math.max(1, Math.ceil(totalItems / entries))}
            onPageChange={handlePageChange}
            onEntriesChange={handleEntriesChange}
            rowsPerPageOptions={[5, 10, 25]}
            minTableWidth={0}
            size="small"
          />
        </Card>
      )}
    </Box>
  );
};

export default PaymentsTable;
